import { ReactNode, createContext, useContext, useReducer } from 'react';
import produce from 'immer';
import { unimplemented } from '@utils/commonUtils';
import { IntegrationType } from '@insights_module/types/Integrations';

type IntegrationsContextKeysType =
  | 'insights.integrations.selected_rows'
  | 'insights.integrations.set_selected_rows'
  | 'insights.integrations.create_tag_modal'
  | 'insights.integrations.delete_tag_modal'
  | 'insights.integrations.edit_tag_modal'
  | 'insights.integrations.tag_to_delete'
  | 'insights.integrations.tag_to_delete_episodes_count'
  | 'insights.integrations.tag_to_edit'
  | 'insights.integrations.tag_to_duplicate'
  | 'insights.integrations.selected_action'
  | 'insights.integrations.search_term'
  | 'insights.integrations.type'
  | 'insights.integrations.show_create_tracking_pixel_modal'
  | 'insights.integrations.show_tracking_pixel_table_modal'
  | 'insights.integrations.description_modal'
  | 'insights.integrations.layout_type';

const INTEGRATIONS_CONTEXT_KEY = {
  SELECTED_ROWS: 'insights.integrations.selected_rows' as IntegrationsContextKeysType,
  SET_SELECTED_ROWS: 'insights.integrations.set_selected_rows' as IntegrationsContextKeysType,
  CREATE_TAG_MODAL: 'insights.integrations.create_tag_modal' as IntegrationsContextKeysType,
  DELETE_TAG_MODAL: 'insights.integrations.delete_tag_modal' as IntegrationsContextKeysType,
  EDIT_TAG_MODAL: 'insights.integrations.edit_tag_modal' as IntegrationsContextKeysType,
  TAG_TO_DELETE: 'insights.integrations.tag_to_delete' as IntegrationsContextKeysType,
  TAG_TO_EDIT: 'insights.integrations.tag_to_edit' as IntegrationsContextKeysType,
  SELECTED_ACTION: 'insights.integrations.selected_action' as IntegrationsContextKeysType,
  TAG_TO_DUPLICATE: 'insights.integrations.tag_to_duplicate' as IntegrationsContextKeysType,
  SEARCH_TERM: 'insights.integrations.search_term' as IntegrationsContextKeysType,
  TYPE: 'insights.integrations.type' as IntegrationsContextKeysType,
  SHOW_CREATE_TRACKING_PIXEL_MODAL:
    'insights.integrations.show_create_tracking_pixel_modal' as IntegrationsContextKeysType,
  SHOW_TRACKING_PIXEL_TABLE_MODAL:
    'insights.integrations.show_tracking_pixel_table_modal' as IntegrationsContextKeysType,
  DESCRIPTION_MODAL: 'insights.integrations.description_modal' as IntegrationsContextKeysType,
  LAYOUT_TYPE: 'insights.integrations.layout_type' as IntegrationsContextKeysType,
};

type IntegrationsContextType = {
  selectedRows: string[];
  isCreateTagModalOpen: boolean;
  isDeleteTagModalOpen: boolean;
  isEditTagModalOpen: boolean;
  tagToDelete: string | null;
  tagToEdit: string | null;
  tagToDuplicate: string | null;
  selectedAction: string | null;
  searchTerm: string;
  type: IntegrationType | null;
  showCreateTrackingPixelModal: boolean;
  showTrackingPixelTableModal: boolean;
  isDescriptionModalOpen: boolean;
  layoutType: string;
  setSelectedRows: (item: string[]) => void;
  setIsCreateTagModalOpen: (val: boolean) => void;
  setIsDeleteTagModalOpen: (val: boolean) => void;
  setTagToDelete: (val: string | null) => void;
  setTagToEdit: (val: string | null) => void;
  setIsEditTagModalOpen: (val: boolean) => void;
  setSelectedAction: (val: string | null) => void;
  setTagToDuplicate: (val: string | null) => void;
  setSearchTerm: (val: string | null) => void;
  setType: (val: IntegrationType | null) => void;
  setShowCreateTrackingPixelModal: (val: boolean) => void;
  setShowTrackingPixelTableModal: (val: boolean) => void;
  setIsDescriptionModalOpen: (val: boolean) => void;
  setLayoutType: (val: string) => void;
};

type Action = {
  type: IntegrationsContextKeysType;
  payload: string[] | boolean | string | null | number | IntegrationType | 'layout' | 'grid';
};

const initialState = {
  selectedRows: [],
  isCreateTagModalOpen: false,
  isDeleteTagModalOpen: false,
  isEditTagModalOpen: false,
  tagToDelete: null,
  tagToEdit: null,
  selectedAction: null,
  tagToDuplicate: null,
  searchTerm: '',
  type: null,
  showCreateTrackingPixelModal: false,
  showTrackingPixelTableModal: false,
  isDescriptionModalOpen: false,
  layoutType: 'table',
  setSelectedRows: unimplemented,
  setIsCreateTagModalOpen: unimplemented,
  setIsDeleteTagModalOpen: unimplemented,
  setIsEditTagModalOpen: unimplemented,
  setTagToDelete: unimplemented,
  setTagToEdit: unimplemented,
  setSelectedAction: unimplemented,
  setTagToDuplicate: unimplemented,
  setSearchTerm: unimplemented,
  setType: unimplemented,
  setShowCreateTrackingPixelModal: unimplemented,
  setShowTrackingPixelTableModal: unimplemented,
  setIsDescriptionModalOpen: unimplemented,
  setLayoutType: unimplemented,
};

const IntegrationsContext = createContext<IntegrationsContextType>(initialState);

const reducer = produce((draft: IntegrationsContextType, action: Action) => {
  const { type, payload } = action;

  switch (type) {
    case INTEGRATIONS_CONTEXT_KEY.SET_SELECTED_ROWS: {
      draft.selectedRows = payload as string[];
      return;
    }
    case INTEGRATIONS_CONTEXT_KEY.CREATE_TAG_MODAL: {
      draft.isCreateTagModalOpen = payload as boolean;
      if (payload) {
        draft.showTrackingPixelTableModal = false;
        draft.showCreateTrackingPixelModal = false;
      }
      return;
    }
    case INTEGRATIONS_CONTEXT_KEY.DELETE_TAG_MODAL: {
      draft.isDeleteTagModalOpen = payload as boolean;
      return;
    }
    case INTEGRATIONS_CONTEXT_KEY.EDIT_TAG_MODAL: {
      draft.isEditTagModalOpen = payload as boolean;
      return;
    }
    case INTEGRATIONS_CONTEXT_KEY.TAG_TO_DELETE: {
      draft.tagToDelete = payload as string | null;
      return;
    }
    case INTEGRATIONS_CONTEXT_KEY.TAG_TO_EDIT: {
      draft.tagToEdit = payload as string | null;
      return;
    }
    case INTEGRATIONS_CONTEXT_KEY.SELECTED_ACTION: {
      draft.selectedAction = payload as string | null;
      return;
    }
    case INTEGRATIONS_CONTEXT_KEY.TAG_TO_DUPLICATE: {
      draft.tagToDuplicate = payload as string | null;
      return;
    }
    case INTEGRATIONS_CONTEXT_KEY.SEARCH_TERM: {
      draft.searchTerm = payload as string;
      return;
    }

    case INTEGRATIONS_CONTEXT_KEY.TYPE: {
      draft.type = payload as IntegrationType;
      return;
    }

    case INTEGRATIONS_CONTEXT_KEY.SHOW_CREATE_TRACKING_PIXEL_MODAL: {
      draft.showCreateTrackingPixelModal = payload as boolean;
      if (payload) {
        draft.showTrackingPixelTableModal = false;
      }
      return;
    }

    case INTEGRATIONS_CONTEXT_KEY.SHOW_TRACKING_PIXEL_TABLE_MODAL: {
      draft.showTrackingPixelTableModal = payload as boolean;
      if (payload) {
        draft.showCreateTrackingPixelModal = false;
      }
      return;
    }

    case INTEGRATIONS_CONTEXT_KEY.DESCRIPTION_MODAL: {
      draft.isDescriptionModalOpen = payload as boolean;
      return;
    }

    case INTEGRATIONS_CONTEXT_KEY.LAYOUT_TYPE: {
      draft.layoutType = payload as string;
      return;
    }

    default:
      return;
  }
});

export const IntegrationsProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
}): JSX.Element => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setSelectedRows = (payload: string[]) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.SET_SELECTED_ROWS, payload: payload });
  };

  const setIsCreateTagModalOpen = (payload: boolean) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.CREATE_TAG_MODAL, payload: payload });
  };

  const setIsDeleteTagModalOpen = (payload: boolean) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.DELETE_TAG_MODAL, payload: payload });
  };

  const setIsEditTagModalOpen = (payload: boolean) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.EDIT_TAG_MODAL, payload: payload });
  };

  const setTagToDelete = (payload: string | null) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.TAG_TO_DELETE, payload: payload });
  };

  const setTagToEdit = (payload: string | null) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.TAG_TO_EDIT, payload: payload });
  };

  const setSelectedAction = (payload: string | null) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.SELECTED_ACTION, payload: payload });
  };

  const setTagToDuplicate = (payload: string | null) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.TAG_TO_DUPLICATE, payload: payload });
  };

  const setSearchTerm = (payload: string | null) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.SEARCH_TERM, payload });
  };

  const setType = (payload: IntegrationType | null) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.TYPE, payload });
  };

  const setShowCreateTrackingPixelModal = (payload: boolean) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.SHOW_CREATE_TRACKING_PIXEL_MODAL, payload });
  };

  const setShowTrackingPixelTableModal = (payload: boolean) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.SHOW_TRACKING_PIXEL_TABLE_MODAL, payload });
  };

  const setIsDescriptionModalOpen = (payload: boolean) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.DESCRIPTION_MODAL, payload });
  };

  const setLayoutType = (payload: string) => {
    dispatch({ type: INTEGRATIONS_CONTEXT_KEY.LAYOUT_TYPE, payload });
  };

  const values = {
    ...state,
    setSelectedRows,
    setIsCreateTagModalOpen,
    setIsDeleteTagModalOpen,
    setIsEditTagModalOpen,
    setTagToDelete,
    setTagToEdit,
    setSelectedAction,
    setTagToDuplicate,
    setSearchTerm,
    setType,
    setShowCreateTrackingPixelModal,
    setShowTrackingPixelTableModal,
    setIsDescriptionModalOpen,
    setLayoutType,
    ...value,
  };
  return <IntegrationsContext.Provider value={values}>{children}</IntegrationsContext.Provider>;
};

export const useIntegrationsContext = () => {
  return useContext(IntegrationsContext);
};
